import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ZoneRegionsModels } from '@share-models';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { ZoneRegionsActions } from '@states/zone-regions/zone-regions.actions';
import { zoneRegionsFeature } from '@states/zone-regions/zone-regions.feature';
import {
  ConfirmDialogModel,
  ConfirmDialogService,
  PreloaderColor,
  UiCameraSelectButtonComponent,
  UiFilterChipComponent,
  UiNoDataComponent,
  UiPreloaderComponent,
  UiToggleComponent,
} from '@ui';
import {
  CellTemplateContext,
  DatasetTableConfig,
  UiDatasetTableComponent,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { IntelligenceModels } from '@models/intelligence.model';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../services/intelligence-tools.service';
import { createZoneRegionDetailTableConfig } from '../../../../../pages/database/pages/zone-regions/zone-region-detail/zone-region-detail-table.util';
import { createZoneRegionsTableConfig } from '../../../../../pages/database/pages/zone-regions/zone-regions-table.util';
import { UiSearchComponent } from '../../../../ui-kit/ui-search/ui-search.component';
import { UiZoneRegionPreviewComponent } from '../../../../ui-kit/ui-zone-region-preview/ui-zone-region-preview.component';
import { take } from 'rxjs';
import { CameraPickerModels } from '@modals/camera-picker-inline/models/models';
import { CameraPickerModalComponent } from '@modals/camera-picker-modal/camera-picker-modal.component';
import { generateCameraIdsIncludesExcludes } from '../../../../../helpers/common.helpers';
import { SharedActions } from '@states/shared/shared.action-types';
import { UtilsService } from '../../../../../edge/utils.service';

@Component({
  selector: 'app-zone-regions-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatMenuModule,
    TranslatePipe,
    UiCameraSelectButtonComponent,
    UiDatasetTableComponent,
    UiFilterChipComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiSearchComponent,
    UiToggleComponent,
    UiZoneRegionPreviewComponent,
  ],
  templateUrl: './zone-regions-result.component.html',
  styleUrls: ['./zone-regions-result.component.scss'],
})
export class ZoneRegionsResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly toolsService = inject(IntelligenceToolsService);
  private readonly store = inject(Store);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly utilsService = inject(UtilsService);

  protected readonly preloaderColor = PreloaderColor;
  protected readonly searchQuery = signal(this.currentListQuery());
  protected readonly selectedCameras = signal<
    Dictionary<CameraPickerModels.SelectedCamera>
  >({});
  protected readonly isFiltering = signal(false);
  protected readonly loadingRegionId = signal<string | null>(null);
  protected readonly deletingRegionId = signal<string | null>(null);
  protected readonly cameraLookup = this.store.selectSignal(CameraSelectors.selectCamerasLookup);
  protected readonly processingRegionIds = this.store.selectSignal(
    zoneRegionsFeature.selectProcessingIds,
  );
  protected readonly processingCameraKeys = this.store.selectSignal(
    zoneRegionsFeature.selectProcessingCameraKeys,
  );

  private readonly countingCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<ZoneRegionsModels.ZoneRegionDocument>>>('countingCell');
  private readonly previewCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<ZoneRegionsModels.ZoneRegionCameraDocument>>>('previewCell');

  protected readonly response = computed<IntelligenceModels.ZoneRegions.Response | null>(() => {
    const result = this.resultsService.current();
    return result?.type === 'zone-regions'
      ? (result.data as IntelligenceModels.ZoneRegions.Response)
      : null;
  });

  protected readonly list = computed(() => {
    const response = this.response();
    return response?.view === 'list' ? response : null;
  });

  protected readonly detail = computed(() => {
    const response = this.response();
    return response?.view === 'detail' ? response : null;
  });

  protected readonly selectedCameraList = computed<
    CameraPickerModels.SelectedCamera[]
  >(() =>
    Object.values(this.selectedCameras()).filter(
      (camera): camera is CameraPickerModels.SelectedCamera => !!camera,
    ),
  );

  private readonly syncListFilters = effect(() => {
    const list = this.list();
    if (!list) return;
    this.searchQuery.set(list.query);
    this.selectedCameras.set(
      Object.fromEntries(
        (list.cameraFilters ?? []).map((camera) => [
          camera.cameraId,
          {
            cameraId: camera.cameraId,
            edgeId: camera.edgeId,
            locationId: camera.locationId,
            name: camera.name,
          },
        ]),
      ),
    );
  });

  private readonly processingIdsSet = computed(() =>
    new Set([
      ...(this.processingRegionIds() ?? []),
      ...([this.loadingRegionId(), this.deletingRegionId()].filter(Boolean) as string[]),
    ]),
  );

  protected readonly listTableConfig = computed<DatasetTableConfig<ZoneRegionsModels.ZoneRegionDocument> | null>(() => {
    const countingCellTemplate = this.countingCellTemplate();
    if (!countingCellTemplate) return null;
    return createZoneRegionsTableConfig({
      processingIdsSet: () => this.processingIdsSet(),
      countingCellTemplate,
      actionIds: ['view', 'delete'],
    });
  });

  protected readonly cameraTableConfig = computed<DatasetTableConfig<ZoneRegionsModels.ZoneRegionCameraDocument> | null>(() => {
    const previewCell = this.previewCellTemplate();
    if (!previewCell) return null;
    return createZoneRegionDetailTableConfig({
      templates: { previewCell },
      cameraLookup: () => this.cameraLookup(),
      processingCameraKeys: () => this.processingCameraKeys() ?? [],
      actionIds: ['delete'],
    });
  });

  protected onSearchQuery(query: string): void {
    if (query === this.searchQuery()) return;
    this.searchQuery.set(query);
    void this.reload();
  }

  protected openSelectCameraModal(): void {
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: { multi: true },
      selectedValue: this.selectedCameras(),
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '70vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(
        (cameras?: Dictionary<CameraPickerModels.SelectedCamera>) => {
          if (!cameras) return;
          this.selectedCameras.set(cameras);
          void this.reload();
        },
      );
  }

  protected removeCameraFilter(cameraId: string): void {
    this.selectedCameras.update((cameras) => {
      const next = { ...cameras };
      delete next[cameraId];
      return next;
    });
    void this.reload();
  }

  protected isProcessing(id: string): boolean {
    return this.processingIdsSet().has(id);
  }

  protected onToggleZoneCounting(
    zoneRegion: ZoneRegionsModels.ZoneRegionDocument,
    countingEnabled: boolean,
  ): void {
    if (this.isProcessing(zoneRegion._id)) return;
    this.store.dispatch(
      ZoneRegionsActions.startSetZoneCounting({
        id: zoneRegion._id,
        countingEnabled,
      }),
    );
  }

  protected onRegionRowAction(event: {
    actionId: string;
    row: ZoneRegionsModels.ZoneRegionDocument;
  }): void {
    if (event.actionId === 'view') void this.openDetails(event.row);
    if (event.actionId === 'delete') void this.deleteZoneRegion(event.row);
  }

  protected onCameraRowAction(event: {
    actionId: string;
    row: ZoneRegionsModels.ZoneRegionCameraDocument;
  }): void {
    if (event.actionId !== 'delete') return;
    const cameraName =
      this.cameraLookup()[event.row.cameraId]?.edgeOnly?.name ??
      this.translateService.instant(
        'ZONE_REGIONS.DIALOGS.DELETE_CAMERA.UNKNOWN_CAMERA',
      );

    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translateService.instant(
          'ZONE_REGIONS.DIALOGS.DELETE_CAMERA.TITLE',
        ),
        msg: this.translateService.instant(
          'ZONE_REGIONS.DIALOGS.DELETE_CAMERA.MESSAGE',
          { cameraName: `"${cameraName}"` },
        ),
        confirm: this.translateService.instant('COMMON.DELETE'),
        disableClose: true,
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.store.dispatch(
          ZoneRegionsActions.startDeleteZoneRegionCamera({
            zoneRegionId: event.row.zoneRegionId,
            cameraId: event.row.cameraId,
            edgeId: event.row.edgeId,
            locationId: event.row.locationId,
            toastContext: 'zone-region-detail',
          }),
        );
      });
  }

  protected async openDetails(zoneRegion: ZoneRegionsModels.ZoneRegionDocument): Promise<void> {
    if (this.loadingRegionId() || this.deletingRegionId()) return;
    this.loadingRegionId.set(zoneRegion._id);
    try {
      const response = await this.toolsService.fetchZoneRegionDetail({
        zoneRegionId: zoneRegion._id,
      });
      if ('data' in response) {
        this.resultsService.setOverlay(response.data, 'zone-regions', zoneRegion.name);
      }
    } finally {
      this.loadingRegionId.set(null);
    }
  }

  protected async deleteZoneRegion(zoneRegion: ZoneRegionsModels.ZoneRegionDocument): Promise<void> {
    if (this.deletingRegionId() || this.loadingRegionId()) return;
    this.deletingRegionId.set(zoneRegion._id);
    try {
      const outcome = await this.toolsService.requestZoneRegionDeletion({
        zoneRegionId: zoneRegion._id,
      });
      if (!('deleted' in outcome)) return;
      const response = await this.toolsService.fetchZoneRegions();
      this.resultsService.replaceCurrentWithZoneRegions(response);
    } finally {
      this.deletingRegionId.set(null);
    }
  }

  private currentListQuery(): string {
    const result = this.resultsService.current();
    if (result?.type !== 'zone-regions') return '';
    const response = result.data as IntelligenceModels.ZoneRegions.Response;
    return response.view === 'list' ? response.query : '';
  }

  private async reload(): Promise<void> {
    const cameraFilter = this.buildCameraFilter();
    this.isFiltering.set(true);
    try {
      const response = await this.toolsService.fetchZoneRegions({
        query: this.searchQuery() || undefined,
        cameraIdsIncludes: cameraFilter.cameraIdsIncludes.length
          ? cameraFilter.cameraIdsIncludes
          : undefined,
        cameraIdsExcludes: cameraFilter.cameraIdsExcludes.length
          ? cameraFilter.cameraIdsExcludes
          : undefined,
      });
      this.resultsService.replaceZoneRegionsInCurrent({
        ...response,
        cameraFilters: this.selectedCameraList().map((camera) => ({
          cameraId: camera.cameraId,
          edgeId: camera.edgeId,
          locationId: camera.locationId,
          name:
            this.cameraLookup()[camera.cameraId]?.edgeOnly?.name ??
            camera.name ??
            camera.cameraId,
        })),
      });
    } catch (error: unknown) {
      this.store.dispatch(
        SharedActions.showMessage({
          error: this.utilsService.errMessage(error as Error),
        }),
      );
    } finally {
      this.isFiltering.set(false);
    }
  }

  private buildCameraFilter(): {
    cameraIdsIncludes: string[];
    cameraIdsExcludes: string[];
  } {
    const selectedCameras = this.selectedCameras();
    if (!Object.keys(selectedCameras).length) {
      return { cameraIdsIncludes: [], cameraIdsExcludes: [] };
    }
    return generateCameraIdsIncludesExcludes(
      selectedCameras,
      Object.keys(this.cameraLookup()),
    );
  }
}
